import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import api from '../api/apiHelper';
import { readApiCache, persistDashboardData } from '../utils/apiCache';

const CACHE_KEY = 'user_dashboard_data';

const UserDashboardContext = createContext(null);

export const UserDashboardProvider = ({ children }) => {
    const [dashboardData, setDashboardData] = useState(() => readApiCache(CACHE_KEY));
    const [isLoading, setIsLoading] = useState(false);
    const isFetching = useRef(false);

    const fetchDashboard = useCallback(async (silent = false) => {
        if (isFetching.current) return;
        isFetching.current = true;
        if (!silent) setIsLoading(true);
        try {
            const { data } = await api.get('user_dashboard_data/', { skipLoading: true });
            setDashboardData(data);
            persistDashboardData(data);
        } catch (err) {
            console.error('UserDashboardContext: fetch error', err);
        } finally {
            isFetching.current = false;
            if (!silent) setIsLoading(false);
        }
    }, []);

    // Self-heal: ekran-konsument woła to przy montażu. Jeśli mamy token, ale brak
    // danych (np. provider zamontował się przed logowaniem), dociągamy je z API.
    const ensureLoaded = useCallback(() => {
        if (isFetching.current) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        if (dashboardData) return;
        fetchDashboard(false);
    }, [dashboardData, fetchDashboard]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        const hasCached = !!readApiCache(CACHE_KEY);
        if (!hasCached) setIsLoading(true);
        fetchDashboard(hasCached);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handler = (e) => {
            if (e.detail?.key !== CACHE_KEY) return;
            if (e.detail.blocking) {
                flushSync(() => setIsLoading(true));
            }
            fetchDashboard(!e.detail.blocking);
        };
        window.addEventListener('api-cache-invalidated', handler);
        return () => window.removeEventListener('api-cache-invalidated', handler);
    }, [fetchDashboard]);

    // Swieze dane pobrane przez inny ekran (Settings/Upgrade/PaymentStatus przez
    // persistDashboardData) wpychamy do stanu na zywo — bez ponownego fetcha.
    useEffect(() => {
        const handler = (e) => {
            if (e.detail?.data) setDashboardData(e.detail.data);
        };
        window.addEventListener('dashboard-data-updated', handler);
        return () => window.removeEventListener('dashboard-data-updated', handler);
    }, []);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState !== 'visible') return;
            const token = localStorage.getItem('token');
            if (!token) return;
            if (!readApiCache(CACHE_KEY)) fetchDashboard(true);
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [fetchDashboard]);

    return (
        <UserDashboardContext.Provider value={{ dashboardData, isLoading, ensureLoaded }}>
            {children}
        </UserDashboardContext.Provider>
    );
};

export const useUserDashboard = () => {
    const ctx = useContext(UserDashboardContext);
    if (!ctx) throw new Error('useUserDashboard must be used within UserDashboardProvider');
    return ctx;
};
